/* @jsx h */
import { defineComponent, h } from "vue";
import {
  walletStampInk,
  walletStampProgress,
  type BrowserWalletPassView,
  type ResolvedWalletField,
} from "@lemmonhq/blogic/wallet-design";
import { QRCodeComponent } from "../QRCode/index";

/**
 * The browser face of a wallet pass: what the Collected wallet shows and what
 * the manager's Browser preview shows, drawn from the same resolved design view
 * (`resolvePassView(..., "browser")`). Everything printed on it — colours,
 * logo, artwork, fields, barcode and links — comes from the view; callers only
 * add live chrome such as the stamp row, progress and selection.
 *
 * Portrait, because a pass is held and scanned upright.
 */

/** Keeps the icon's filled Material style consistent across every token. */
const FILLED_ICON = {
  fontVariationSettings: "'FILL' 1, 'wght' 600, 'GRAD' 0, 'opsz' 24",
};

/**
 * The loyalty stamps — the browser twin of the stamps DBOS bakes into Apple
 * and Google artwork (docs/plans/wallet_passes/09-stamp-band.md). One slot per
 * coin of the reward threshold, then the reward, so the card shows progress
 * rather than repeating the balance. There is no background strip: every token
 * sits on its own solid disc in the pass colour, so it reads over any photo.
 * Layout comes from walletStampProgress, shared with DBOS.
 */
const StampBand = defineComponent<{
  balance: number;
  target: number;
  icon: string;
  background: string;
  foreground: string;
  alignment?: "left" | "center" | "right";
  animate?: boolean;
}>({
  name: "WalletPassStampBand",
  props: [
    "balance",
    "target",
    "icon",
    "background",
    "foreground",
    "alignment",
    "animate",
  ],
  setup(props) {
    return () => {
      const progress = walletStampProgress(props.balance, props.target);
      const ink = walletStampInk(props.background, props.foreground);
      const dense = (progress.rows[0] ?? 0) > 5;
      const token = dense ? "h-7 w-7 text-[16px]" : "h-9 w-9 text-[20px]";

      /** The solid pass-colour disc every token stands on. */
      const back = (key: string | number, content: any, badge?: any) => (
        <span
          key={key}
          data-stamp-slot={key}
          class="relative shrink-0 rounded-full p-[3px] shadow-md"
          style={{ backgroundColor: props.background }}
        >
          {content}
          {badge}
        </span>
      );

      const filledDisc = (glyph: string, filledIcon: boolean, pop = false) => (
        <span
          class={`flex items-center justify-center rounded-full ${token}`}
          style={{ backgroundColor: ink, color: props.background }}
        >
          <span
            class={[
              "material-symbols-outlined leading-none",
              pop ? "claim-stamp-pop" : "",
            ]}
            style={{
              ...(filledIcon ? FILLED_ICON : {}),
              fontSize: "inherit",
            }}
          >
            {glyph}
          </span>
        </span>
      );

      const reward = back(
        "reward",
        progress.ready ? (
          filledDisc("redeem", false)
        ) : (
          <span
            class={`flex items-center justify-center rounded-full border-2 ${token}`}
            style={{ borderColor: ink, color: ink }}
          >
            <span
              class="material-symbols-outlined leading-none"
              style={{ fontSize: "inherit" }}
            >
              redeem
            </span>
          </span>
        ),
        progress.rewardsReady > 1 && (
          <span
            class="absolute -right-2 -top-2 rounded-full px-1.5 text-[10px] font-bold leading-4 ring-2"
            style={{
              backgroundColor: ink,
              color: props.background,
              // The ring separates the badge from the token behind it.
              ["--tw-ring-color" as string]: props.background,
            }}
          >
            ×{progress.rewardsReady}
          </span>
        ),
      );

      let index = 0;
      return (
        <div
          aria-hidden="true"
          data-wallet-stamp-band={progress.mode}
          class="w-full px-4 py-2.5"
        >
          {progress.mode === "bar" ? (
            // Text and a thin bar can't carry their own contrast like a
            // token, so bar mode keeps a content-sized pill behind them.
            <div
              class="mx-auto flex max-w-[16rem] items-center gap-3 rounded-full p-1.5 pr-2 shadow-md"
              style={{ backgroundColor: props.background }}
            >
              {filledDisc(props.icon, true)}
              <div class="min-w-0 flex-1" style={{ color: ink }}>
                <p class="font-mono text-sm font-bold leading-none tabular-nums">
                  {progress.filled} / {progress.required}
                </p>
                <div
                  class="mt-1.5 h-1.5 overflow-hidden rounded-full"
                  style={{ backgroundColor: `${ink}4D` }}
                >
                  <div
                    class="h-full rounded-full"
                    style={{
                      backgroundColor: ink,
                      width: `${Math.round((progress.filled / progress.required) * 100)}%`,
                    }}
                  />
                </div>
              </div>
              {reward}
            </div>
          ) : (
            <div
              class={`flex flex-col gap-2 ${
                props.alignment === "left"
                  ? "items-start"
                  : props.alignment === "right"
                    ? "items-end"
                    : "items-center"
              }`}
            >
              {progress.rows.map((count, row) => (
                <div
                  key={row}
                  class={`flex justify-center ${dense ? "gap-1" : "gap-2"}`}
                >
                  {Array.from({ length: count }, () => {
                    const slot = index++;
                    if (slot === progress.required) return reward;
                    return slot < progress.filled
                      ? back(slot, filledDisc(props.icon, true, props.animate))
                      : back(
                          slot,
                          <span
                            class={`block rounded-full border-2 border-dashed ${token}`}
                            style={{ borderColor: ink }}
                          />,
                        );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };
  },
});

/** A label above its value, the way a printed pass sets them. */
const Field = defineComponent<{
  field: ResolvedWalletField;
  labelColor: string;
  align?: "left" | "right";
  size?: "sm" | "lg";
  wrap?: boolean;
}>({
  name: "WalletPassField",
  props: ["field", "labelColor", "align", "size", "wrap"],
  setup(props) {
    return () => (
      <div
        data-wallet-field={props.field.key}
        class={`min-w-0 ${props.align === "right" ? "text-right" : ""}`}
      >
        <p
          class="font-mono text-[10px] uppercase tracking-widest truncate"
          style={{ color: props.labelColor }}
        >
          {props.field.label}
        </p>
        <p
          class={
            props.size === "lg"
              ? "font-mono text-3xl font-bold mt-1 leading-none tabular-nums truncate"
              : `font-mono text-sm mt-0.5 ${props.wrap ? "whitespace-normal leading-snug" : "truncate"}`
          }
        >
          {props.field.value}
        </p>
      </div>
    );
  },
});

export type WalletPassCardProgress = {
  value: number;
  max: number;
  label: string;
};

export type WalletPassCardProps = {
  view: BrowserWalletPassView;
  /** Material glyph used in the header when the design has no logo, and for stamps. */
  icon: string;
  /** Header text when the design's logo text resolves empty. */
  fallbackTitle: string;
  /** Coins held, drawn in the stamp band when the design enables stamps. */
  stampCount?: number | null;
  /** Reward threshold: the band's slot count. Defaults to the progress max. */
  stampTarget?: number | null;
  animateStamps?: boolean;
  progress?: WalletPassCardProgress | null;
  /** Replaces the stamp row in the middle of the pass (e.g. a large QR). */
  center?: () => any;
  /** Overrides the design's barcode for the small code on the panel edge. */
  qrValue?: string | null;
  /** Drop the width cap and fill the container. */
  wide?: boolean;
  /** Fixed panel height class, so cards in a deck share proportions. */
  panelClass?: string;
  /** Scanner/back face: hide front-only detail and give the reward full width. */
  back?: boolean;
  onSelect?: () => void;
};

export const WalletPassCard = defineComponent<WalletPassCardProps>({
  name: "WalletPassCard",
  props: [
    "view",
    "icon",
    "fallbackTitle",
    "stampCount",
    "stampTarget",
    "animateStamps",
    "progress",
    "center",
    "qrValue",
    "wide",
    "panelClass",
    "back",
    "onSelect",
  ],
  setup(props) {
    return () => {
      const { view } = props;
      const interactive = !!props.onSelect;
      const artwork = view.assets.artwork;
      const logo = view.assets.logo ?? view.assets.icon;
      const qrValue = props.back
        ? null
        : props.qrValue !== undefined
          ? props.qrValue
          : view.barcode?.symbology === "qr"
            ? view.barcode.message
            : null;
      const progress =
        !props.back && view.show_progress ? props.progress : null;
      const stampTarget = props.stampTarget ?? props.progress?.max ?? 0;
      const showStamps =
        !!view.stamps && props.stampCount != null && stampTarget > 0;

      return (
        <div
          role={interactive ? "button" : undefined}
          tabindex={interactive ? 0 : undefined}
          onClick={() => props.onSelect?.()}
          onKeydown={(e: KeyboardEvent) => {
            if (interactive && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              props.onSelect?.();
            }
          }}
          class={[
            "relative overflow-hidden rounded-3xl shadow-xl select-none",
            props.wide
              ? "w-full h-full"
              : "aspect-[5/7] w-full max-w-[20rem] mx-auto",
            interactive
              ? "cursor-pointer transition-transform duration-200 hover:-translate-y-1 focus:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
              : "",
          ]}
          // Pass colours are design data, not theme tokens.
          style={{
            backgroundColor: view.colors.background,
            color: view.colors.foreground,
          }}
        >
          {artwork && (
            <img
              src={artwork}
              alt=""
              aria-hidden="true"
              class="absolute inset-0 w-full h-full object-cover"
              style={
                view.asset_refs.artwork?.focal_point
                  ? {
                      objectPosition: `${view.asset_refs.artwork.focal_point.x * 100}% ${view.asset_refs.artwork.focal_point.y * 100}%`,
                    }
                  : undefined
              }
            />
          )}

          {/* Over arbitrary photography contrast is absolute rather than
              thematic, so the scrim is a fixed black. */}
          <div
            aria-hidden="true"
            class={
              artwork
                ? "absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-black/70"
                : "absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/20"
            }
          />

          <div class="relative h-full flex flex-col">
            <div class="flex items-start justify-between gap-3 p-5">
              <div class="flex items-center gap-2 min-w-0">
                {logo ? (
                  <img
                    src={logo}
                    alt=""
                    aria-hidden="true"
                    class="h-6 max-w-[5rem] shrink-0 object-contain"
                  />
                ) : (
                  <span class="material-symbols-outlined text-xl shrink-0 drop-shadow">
                    {props.icon}
                  </span>
                )}
                <p class="font-mono text-[11px] uppercase tracking-[0.2em] truncate drop-shadow">
                  {view.logo_text || props.fallbackTitle}
                </p>
              </div>
              {view.primary && (
                <div class="shrink-0 drop-shadow">
                  <Field
                    field={view.primary}
                    labelColor={view.colors.label}
                    align="right"
                    size="lg"
                  />
                </div>
              )}
            </div>

            <div
              class={`flex-1 flex items-center justify-center min-h-0 ${props.center ? "px-5" : ""}`}
            >
              {props.center ? (
                props.center()
              ) : showStamps ? (
                <StampBand
                  balance={props.stampCount!}
                  target={stampTarget}
                  icon={props.icon}
                  background={view.colors.background}
                  foreground={view.colors.foreground}
                  alignment={view.stamps?.alignment}
                  animate={props.animateStamps}
                />
              ) : null}
            </div>

            {/* A flat tint keeps small type legible over any photograph for one
                paint, on every card in every slot of a deck. */}
            <div
              class={`mt-auto p-5 space-y-3 relative ${props.panelClass ?? ""} ${
                qrValue ? "pt-9" : ""
              } ${artwork ? "bg-black/55 border-t border-white/15" : "bg-black/15 border-t border-current/15"}`}
            >
              {qrValue && (
                <div class="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-1 shadow-lg ring-1 ring-black/10">
                  <div class="w-12 h-12">
                    {/* Rasterised: a deck's worth of SVG modules is thousands
                        of nodes, all sub-pixel at this size anyway. */}
                    <QRCodeComponent
                      key={qrValue}
                      url={qrValue}
                      raster={true}
                    />
                  </div>
                </div>
              )}

              {progress && (
                <div
                  class="h-1 rounded-full bg-current/25 overflow-hidden"
                  role="progressbar"
                  aria-label={progress.label}
                  aria-valuenow={Math.min(progress.value, progress.max)}
                  aria-valuemin={0}
                  aria-valuemax={progress.max}
                >
                  <div
                    class="h-full rounded-full bg-current transition-[width] duration-500"
                    style={{
                      width: `${
                        progress.max > 0
                          ? Math.min(
                              100,
                              Math.round((progress.value / progress.max) * 100),
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
              )}

              {!props.back &&
                view.secondary.some((field) => field.key !== "reward") && (
                  <div class="flex items-end justify-between gap-3">
                    {view.secondary
                      .filter((field) => field.key !== "reward")
                      .map((field, index, fields) => (
                        <Field
                          key={field.key}
                          field={field}
                          labelColor={view.colors.label}
                          align={
                            index > 0 && index === fields.length - 1
                              ? "right"
                              : "left"
                          }
                        />
                      ))}
                  </div>
                )}

              {view.secondary
                .filter((field) => field.key === "reward")
                .map((field) => (
                  <Field
                    key={field.key}
                    field={field}
                    labelColor={view.colors.label}
                    wrap={props.back}
                  />
                ))}

              {view.links.length > 0 && (
                <div class="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px]">
                  {view.links.map((link) => (
                    <a
                      key={link.key}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="underline underline-offset-2 truncate"
                      onClick={(e: MouseEvent) => e.stopPropagation()}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
  },
});
